import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import { Box, Hidden, IconButton, Stack, Typography } from "@mui/material"
import { ReactElement, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useScreenSize } from "../../../hooks/useScreenSize"
import {
    AMEND_SINGLE_APPLICATION_BASE,
    INSTALLER_SINGLE_APPLICATION_BASE,
} from "../../../router/routes"
import { IFormData } from "../../../services/middleware/types/amendApplication/launchAmendmentTypes"
import { GT_368_FORM_CLASS } from "../../../services/middleware/types/commissioning/installer/launchCxFormTypes"
import { IFormProperties } from "../../../services/middleware/types/singleApplication/getSchemaTypes"
import { scrollToTop } from "../../../utils/submitApplication/submitApplicationUtils"
import AppButton from "../../buttons/appButton"
import AppStepper from "../../dataDisplay/stepper/appStepper"
import FormHeader from "../../formHeader/formHeader"
import Drawer from "../../popUps/drawer/drawer"
import ProcessOverviewTips, {
    AMEND_PROCESS_STEPS,
    CX_PROCESS_STEPS,
    CX_TIPS_Q_AND_A,
} from "../../popUps/processOverview/processOverviewTips"
import BaseFormGenerator from "../../submitApplication/single/formGenerator/basicForm/baseFormGenerator"
import { APPLICATION_SPACING } from "../../submitApplication/single/formGenerator/formDispatcher"
import { FILE_URL_KEY } from "../../submitApplication/single/inputGenerator/inputGenerator"
import AttachmentArrayBase from "../../submitApplication/single/recognisedForms/attachmentArrayBase"
import AppPaper from "../../surfaces/appPaper"
import DisplayIf from "../../wrappers/displayIf"
import DeclarationSubmit from "../common/declarationSubmit"
import {
    POP_UP_SUBTITLE_AMEND,
    POP_UP_SUBTITLE_OVERVIEW,
    POP_UP_TITLE_AMEND,
    POP_UP_TITLE_OVERVIEW,
    SITE_DATA_KEY,
    SUPPORTING_FILES_KEY,
} from "../common/keys"
import { TCxStepIndexMap } from "../types"

const GT_368_MIN_FILES = 1
const GT_368_MAX_FILES = 20
const INSTALLATION_DETAILS_TITLE = "Installation Details"
const SUPPORTING_FILES_TITLE = "Supporting Files"
const DECLARATION_TITLE = "Declaration"
const SITE_SUBTITLE =
    "To get started, please fill out the following details about the installation site. This form captures some basic details about the installation and does a few checks to ensure it is compliant!"
const DEVICE_SUBTITLE =
    "As part of the commissioning process, you are required to fill out the below form for each power generating module in your installation"
const ATTACHMENT_SUBTITLE =
    "Please upload any attachments required to support your commissioning information"
const DECLARATION_SUBTITLE =
    "Acknowledgement and declaration of the information provided"

export const DEVICE_INDEX_DELIMITER = "||"

interface IGt368FormProps {
    siteDataFormProperties: IFormProperties
    deviceDataFormProperties: IFormProperties
    devices: string[]
    formVersion: number
    applicationId: string
    initialValues: IFormData
    customerAddress: string
    dno: string
}

const Gt368Form = ({
    siteDataFormProperties,
    deviceDataFormProperties,
    devices,
    formVersion,
    applicationId,
    initialValues,
    customerAddress,
    dno,
}: IGt368FormProps): ReactElement => {
    const [currentStepIndex, setCurrentStepIndex] = useState(0)
    const [formData, setFormData] = useState<IFormData>(initialValues)
    const [supplementingInfo, setSupplementingInfo] =
        useState<Record<string, unknown>>(initialValues)
    const [showAmendProcess, setAmendShowProcess] = useState(false)
    const [showCxProcess, setCxShowProcess] = useState(false)
    const { isMob } = useScreenSize()
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)

    const toggleDrawer = () => () => {
        setOpen(!open)
    }

    const displayStepTitles = [
        { title: INSTALLATION_DETAILS_TITLE, subtitle: SITE_SUBTITLE },
        ...devices.map((device) => ({
            title: device,
            subtitle: DEVICE_SUBTITLE,
        })),
        {
            title: SUPPORTING_FILES_TITLE,
            subtitle: ATTACHMENT_SUBTITLE,
        },
        { title: DECLARATION_TITLE, subtitle: DECLARATION_SUBTITLE },
    ]

    const deviceStartingIndex = 1
    const lastStepIndex = devices.length + deviceStartingIndex + 1
    const penUltimateStepIndex = lastStepIndex - 1
    const deviceStepIndexDetailsMap = devices.reduce<TCxStepIndexMap>(
        (obj, device, index) => ({
            ...obj,
            [`${device}||${index}`]: {
                title: "title",
                subtitle: "subtitle",
                device,
                formKey: `${device}${DEVICE_INDEX_DELIMITER}${index}`,
                stepIndex: index + deviceStartingIndex,
            },
        }),
        {}
    )

    return (
        <>
            <AppPaper>
                <AppStepper
                    stepLabels={displayStepTitles.map((title) => title.title)}
                    currentStepIndex={currentStepIndex}
                />
            </AppPaper>
            <AppPaper>
                <Stack
                    spacing={APPLICATION_SPACING}
                    paddingBottom={APPLICATION_SPACING}
                    display="flex"
                    flexDirection={isMob ? "column" : "row"}
                    justifyContent="space-between"
                >
                    <Hidden mdUp>
                        <>
                            <Drawer open={open} toggleDrawer={toggleDrawer()}>
                                <AppButton
                                    sx={
                                        isMob
                                            ? { mt: 0, mr: 0 }
                                            : { mt: 0, mr: 1 }
                                    }
                                    onClick={() => setCxShowProcess(true)}
                                    variant="outlined"
                                    color="primary"
                                    endIcon={<LightbulbOutlinedIcon />}
                                >
                                    Process Overview
                                </AppButton>
                                <AppButton
                                    sx={
                                        isMob
                                            ? { mt: 1, mr: 0 }
                                            : { mt: 0, mr: 0 }
                                    }
                                    onClick={() => setAmendShowProcess(true)}
                                    variant="outlined"
                                    color="primary"
                                >
                                    Application details changed post approval?
                                </AppButton>
                            </Drawer>
                            <Box sx={{ position: "absolute", right: 15 }}>
                                <IconButton
                                    aria-label="more"
                                    onClick={toggleDrawer()}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            </Box>
                        </>
                    </Hidden>

                    <Box maxWidth={{ md: "100%", lg: "50%" }}>
                        <FormHeader
                            header={displayStepTitles[currentStepIndex].title}
                            subHeader={
                                displayStepTitles[currentStepIndex].subtitle
                            }
                            index={currentStepIndex + 1}
                        />
                    </Box>
                    <Hidden mdDown>
                        <DisplayIf condition={currentStepIndex === 0}>
                            <Stack
                                display="flex"
                                flexDirection={isMob ? "column" : "row"}
                                height="100%"
                            >
                                <AppButton
                                    sx={{ mt: 0, mr: 1 }}
                                    onClick={() => setCxShowProcess(true)}
                                    variant="outlined"
                                    color="primary"
                                    endIcon={<LightbulbOutlinedIcon />}
                                >
                                    Process Overview
                                </AppButton>
                                <AppButton
                                    sx={{ mt: 0, mr: 0 }}
                                    onClick={() => setAmendShowProcess(true)}
                                    variant="outlined"
                                    color="primary"
                                >
                                    Application details changed post approval?
                                </AppButton>
                            </Stack>
                        </DisplayIf>
                    </Hidden>
                    <ProcessOverviewTips
                        popUpTitle={POP_UP_TITLE_OVERVIEW}
                        popUpSubTitle={POP_UP_SUBTITLE_OVERVIEW}
                        isOpen={showCxProcess}
                        close={() => setCxShowProcess(false)}
                        processSteps={CX_PROCESS_STEPS}
                        tipsQAndA={CX_TIPS_Q_AND_A}
                    />
                    <ProcessOverviewTips
                        popUpTitle={POP_UP_TITLE_AMEND}
                        popUpSubTitle={POP_UP_SUBTITLE_AMEND}
                        isOpen={showAmendProcess}
                        close={() => setAmendShowProcess(false)}
                        processSteps={AMEND_PROCESS_STEPS}
                        action={() =>
                            navigate(
                                `${AMEND_SINGLE_APPLICATION_BASE}/${applicationId}`
                            )
                        }
                        actionButtonLabel="Amend Application"
                        actionButton
                    />
                </Stack>

                <DisplayIf condition={currentStepIndex === 0}>
                    <Stack spacing={1} paddingBottom={5}>
                        <Typography variant="subtitle2">
                            Application ID: {applicationId}
                        </Typography>
                        <Typography variant="subtitle2">
                            Address: {customerAddress}
                        </Typography>
                    </Stack>
                </DisplayIf>

                <Stack spacing={APPLICATION_SPACING}>
                    <DisplayIf condition={currentStepIndex === 0}>
                        <BaseFormGenerator
                            formKey={SITE_DATA_KEY}
                            formProperties={siteDataFormProperties}
                            formData={formData}
                            setFormData={setFormData}
                            setCurrentStepIndex={setCurrentStepIndex}
                            isFirstStep
                            onClickBackFirstStep={() => {
                                scrollToTop(document)
                                navigate(
                                    `${INSTALLER_SINGLE_APPLICATION_BASE}/${applicationId}`
                                )
                            }}
                            isAmendment={false}
                        />
                    </DisplayIf>
                    {Object.entries(deviceStepIndexDetailsMap).map(
                        ([, details]) => (
                            <DisplayIf
                                key={details.formKey}
                                condition={
                                    currentStepIndex === details.stepIndex
                                }
                            >
                                <BaseFormGenerator
                                    formKey={details.formKey}
                                    formProperties={deviceDataFormProperties}
                                    formData={formData}
                                    setFormData={setFormData}
                                    setCurrentStepIndex={setCurrentStepIndex}
                                    isFirstStep={false}
                                    isAmendment={false}
                                />
                            </DisplayIf>
                        )
                    )}

                    <DisplayIf
                        condition={currentStepIndex === penUltimateStepIndex}
                    >
                        <AttachmentArrayBase
                            formKey={SUPPORTING_FILES_KEY}
                            minItems={GT_368_MIN_FILES}
                            fileUrlKey={FILE_URL_KEY}
                            imageMode
                            initialUploads={
                                (supplementingInfo[
                                    SUPPORTING_FILES_KEY
                                ] as []) || []
                            }
                            maxItems={GT_368_MAX_FILES}
                            setFormData={setFormData}
                            setCurrentStepIndex={setCurrentStepIndex}
                            setSupplementingInfo={setSupplementingInfo}
                            isFirstStep={false}
                            commissioning
                        />
                    </DisplayIf>
                    <DisplayIf condition={currentStepIndex === lastStepIndex}>
                        <DeclarationSubmit
                            formClass={GT_368_FORM_CLASS}
                            setCurrentStepIndex={setCurrentStepIndex}
                            formVersion={formVersion}
                            applicationId={applicationId}
                            formData={formData}
                            dno={dno}
                        />
                    </DisplayIf>
                </Stack>
            </AppPaper>
        </>
    )
}
export default Gt368Form
